/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 */

import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useGetUsersFileMgmtQuery, useCreateUsersFileMgmtMutation, useUpdateUsersFileMgmtMutation } from "../../../../store/api/admin/adminApiSlice";
import { setIsAddNewUserFileMgmt, setSelectedUserFileMgmt } from "../../../../store/api/admin/reducers";
import { userFileMgmtSelector, isAddNewUserFileMgmtSelector } from "../../../../store/api/admin/selectors";
import { IUserFileMgmt } from "../../../../store/api/admin/types";
import { setIsLoadingStatus, setBasicDialogOpenFor, setOpenToast } from "../../../../store/helpers/helperReducers";
import { useStoreDispatch } from "../../../../store/hooks";
import { isEqualString, errorMessage } from "../../../../utils/helpers";
import { isSpecialChars } from "../../../../utils/validations";
import { IKeyValue } from "../../types";
import { initialState } from "../../../../store/api/admin/initialState";

export const useFormDataUserFileMgmt = () => {
  const dispatch = useStoreDispatch();
  const { data = [], refetch } = useGetUsersFileMgmtQuery();
  const selectedUserFileMgmt = useSelector(userFileMgmtSelector);
  const isAddNew = useSelector(isAddNewUserFileMgmtSelector);
  const [formData, setFormData] = useState<IUserFileMgmt>(selectedUserFileMgmt);
  const [errMsg, setErrMsg] = useState<IKeyValue>({});

  const [createUsersFileMgmt, addnewUserStatus] = useCreateUsersFileMgmtMutation();
  const [updateUsersFileMgmt, updateUserStatus] = useUpdateUsersFileMgmtMutation();

  useEffect(() => {
    dispatch(setIsLoadingStatus({ userFileMgmt: addnewUserStatus.isLoading || updateUserStatus.isLoading }));
  }, [addnewUserStatus.isLoading, dispatch, updateUserStatus.isLoading]);

 
  const userIsAlreadyExist = (useName: string) => {
    let isAlreadyExist: boolean = false;
    isAlreadyExist = data?.length > 0 && data.some((item: IUserFileMgmt) => isEqualString(item.username, useName));
    return isAlreadyExist;
  };

  const submitHandler = async () => {
    if (isAddNew && userIsAlreadyExist(formData.username)) {
      setErrMsg({ username: "Username is already in use" });
      return;
    }
    try {
      let result: any;
      if (isAddNew) {
        result = await createUsersFileMgmt(formData);
      } else {
        result = await updateUsersFileMgmt(formData);
      }

      if (!!result?.error) throw result?.error;
      dispatch(setBasicDialogOpenFor(undefined));
      dispatch(
        setOpenToast({
          openToast: true,
          toastType: "success",
          message: isAddNew
            ? `The user "${formData.username}" has been successfully created.`
            : `The user ${formData.username} has been successfully updated.`,
        })
      );
      refetch();
      dispatch(setIsAddNewUserFileMgmt(false));
      dispatch(setSelectedUserFileMgmt(initialState.selectedUserFileMgmt));
    } catch (error: any) {
      dispatch(setOpenToast({ openToast: true, toastType: "error", message: errorMessage(error) }));
    }
  };
  const onBlurHandler = (event: any) => {
    const { name, value } = event.target;
    let isUserExist: boolean = false;

    if (name === "username") {
      if (!!value && isAddNew) {
        isUserExist = userIsAlreadyExist(value);
       
        isUserExist && setErrMsg({ [name]: `User Name Already Exist!` });
        if (value === "AMS"){
          setErrMsg({ ...errMsg, [name]: `User Name AMS is not allowed!` });
        }
      } else if (!!value && !isAddNew) {
        setErrMsg({ ...errMsg, [name]: "" });
      } else if (!!!value) {
        setErrMsg({ ...errMsg, [name]: `User Name is required!` });
      }
      if (isSpecialChars(value)) {
        setErrMsg({ username: "Special characters are not permitted in this field." });
        return;
      }
    }
  };

  return {
    dispatch,
    formData,
    setFormData,
    errMsg,
    setErrMsg,
    submitHandler,
    onBlurHandler,
    isAddNew
  };
};
